/**
 * PaymentSheet — Paystack-style mock checkout used across:
 *   - Paid event join
 *   - Online / hybrid service booking
 *   - Premium upgrade
 *   - Verification fee
 *
 * Two methods: Card (16-digit) and Bank Transfer (mock account + countdown).
 * Returns success via onSuccess({ method, reference }).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { X, CreditCard, Building2, Copy, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";

const fmt = (n) => "₦" + Number(n || 0).toLocaleString();
const TRANSFER_WINDOW_SEC = 30 * 60; // 30-min mock window

const newReference = (prefix = "PSK") =>
    `${prefix}-${Math.random().toString(36).slice(2, 6).toUpperCase()}-${Date.now().toString(36).toUpperCase().slice(-4)}`;

const luhnValid = (raw) => {
    const s = String(raw || "").replace(/\s+/g, "");
    if (!/^\d{16}$/.test(s)) return false;
    let sum = 0, dbl = false;
    for (let i = s.length - 1; i >= 0; i--) {
        let d = +s[i];
        if (dbl) { d *= 2; if (d > 9) d -= 9; }
        sum += d;
        dbl = !dbl;
    }
    return sum % 10 === 0;
};

const PaymentSheet = ({
    open,
    onClose,
    onSuccess,
    amount,
    title = "Pay",
    summary,           // optional jsx shown above method tabs
    customerEmail,
    purposeLabel = "payment",  // e.g. "service booking", "event ticket"
    refundNote,        // optional jsx shown below the action button
}) => {
    const [tab, setTab] = useState("card");
    const [card, setCard] = useState({ number: "", name: "", expiry: "", cvv: "" });
    const [cardErr, setCardErr] = useState(null);
    const [phase, setPhase] = useState("idle"); // idle | charging | confirming | success
    const [account] = useState(() => ({
        number: String(9_000_000_000 + Math.floor(Math.random() * 999_999_999)).slice(0, 10),
        bank: "Wema Bank",
        name: "NEYRA / PAYSTACK COLLECT",
    }));
    const [secondsLeft, setSecondsLeft] = useState(TRANSFER_WINDOW_SEC);
    const successRef = useRef(false);

    // Reset on open
    useEffect(() => {
        if (open) {
            setTab("card");
            setCard({ number: "", name: "", expiry: "", cvv: "" });
            setCardErr(null);
            setPhase("idle");
            setSecondsLeft(TRANSFER_WINDOW_SEC);
            successRef.current = false;
        }
    }, [open]);

    // Countdown
    useEffect(() => {
        if (!open || tab !== "transfer" || phase !== "idle") return;
        if (secondsLeft <= 0) return;
        const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [open, tab, phase, secondsLeft]);

    const mmss = useMemo(() => {
        const m = Math.floor(secondsLeft / 60).toString().padStart(2, "0");
        const s = (secondsLeft % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    }, [secondsLeft]);

    if (!open) return null;

    const finishWith = (method) => {
        if (successRef.current) return;
        successRef.current = true;
        const reference = newReference(method === "card" ? "PSK-CARD" : "PSK-TRF");
        setPhase("success");
        setTimeout(() => {
            onSuccess?.({ method, reference, amount });
        }, 700);
    };

    const handleCardPay = () => {
        const num = card.number.replace(/\s+/g, "");
        if (!luhnValid(num)) {
            setCardErr("Invalid card number");
            return;
        }
        if (!/^\d{2}\/\d{2}$/.test(card.expiry)) {
            setCardErr("Expiry must be MM/YY");
            return;
        }
        if (!/^\d{3,4}$/.test(card.cvv)) {
            setCardErr("Invalid CVV");
            return;
        }
        if (!card.name.trim()) {
            setCardErr("Enter the cardholder name");
            return;
        }
        setCardErr(null);
        setPhase("charging");
        setTimeout(() => finishWith("card"), 1100);
    };

    const handleClaimedTransfer = () => {
        setPhase("confirming");
        setTimeout(() => finishWith("transfer"), 1500);
    };

    const formatCardNumber = (raw) =>
        raw.replace(/\D/g, "").slice(0, 16).replace(/(\d{4})(?=\d)/g, "$1 ");

    const formatExpiry = (raw) => {
        const d = raw.replace(/\D/g, "").slice(0, 4);
        if (d.length <= 2) return d;
        return `${d.slice(0, 2)}/${d.slice(2)}`;
    };

    return (
        <div
            className="absolute inset-0 z-50 flex items-end"
            data-testid="payment-sheet"
            role="dialog"
            aria-modal="true"
        >
            <button
                onClick={() => phase === "idle" && onClose?.()}
                aria-label="Close"
                className="absolute inset-0 bg-black/55 animate-fade-in"
                data-testid="payment-sheet-scrim"
            />
            <div
                className="relative z-10 w-full max-h-[88vh] flex flex-col bg-card border-t border-border rounded-t-[28px] shadow-[0_-20px_48px_-12px_rgba(0,0,0,0.4)] animate-slide-up pb-[max(20px,env(safe-area-inset-bottom))]"
            >
                <div className="flex justify-center pt-2.5 pb-1 shrink-0">
                    <span className="h-1.5 w-10 rounded-full bg-muted" />
                </div>

                {/* Header */}
                <div className="px-5 pt-1.5 pb-3 flex items-center justify-between border-b border-border/60 shrink-0">
                    <div className="min-w-0">
                        <h2 className="font-display text-[16px] font-semibold leading-tight">
                            {title}
                        </h2>
                        <p className="text-[11.5px] text-muted-foreground inline-flex items-center gap-1.5 mt-0.5">
                            <ShieldCheck size={11} className="text-primary" />
                            Secured checkout
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Amount</div>
                        <div className="font-display text-[15px] font-semibold" data-testid="payment-sheet-amount">
                            {fmt(amount)}
                        </div>
                    </div>
                    <button
                        onClick={() => phase === "idle" && onClose?.()}
                        className="ml-2 h-9 w-9 flex items-center justify-center rounded-xl hover:bg-muted disabled:opacity-50"
                        disabled={phase !== "idle"}
                        aria-label="Close"
                        data-testid="payment-sheet-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto no-scrollbar px-5 pt-3">
                    {summary && <div className="mb-3" data-testid="payment-sheet-summary">{summary}</div>}

                    {/* Phase: success */}
                    {phase === "success" && (
                        <div className="py-8 flex flex-col items-center text-center" data-testid="payment-sheet-success">
                            <span
                                className="h-16 w-16 rounded-full flex items-center justify-center text-primary-foreground"
                                style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))" }}
                            >
                                <CheckCircle2 size={32} />
                            </span>
                            <h3 className="mt-4 font-display text-[18px] font-semibold">Payment successful</h3>
                            <p className="mt-1 text-[12.5px] text-muted-foreground">Finalizing your {purposeLabel}…</p>
                        </div>
                    )}

                    {/* Phase: charging or confirming */}
                    {(phase === "charging" || phase === "confirming") && (
                        <div className="py-8 flex flex-col items-center text-center" data-testid="payment-sheet-processing">
                            <Loader2 size={28} className="text-primary animate-spin" />
                            <p className="mt-4 text-[13px] font-medium">
                                {phase === "charging" ? "Charging card…" : "Confirming payment…"}
                            </p>
                            <p className="mt-1 text-[11.5px] text-muted-foreground">
                                Please don't close this window.
                            </p>
                        </div>
                    )}

                    {phase === "idle" && (
                        <>
                            {/* Tabs */}
                            <div className="flex gap-2 mb-3" data-testid="payment-sheet-tabs">
                                <TabBtn
                                    testId="payment-tab-card"
                                    active={tab === "card"}
                                    onClick={() => setTab("card")}
                                    icon={CreditCard}
                                    label="Card"
                                />
                                <TabBtn
                                    testId="payment-tab-transfer"
                                    active={tab === "transfer"}
                                    onClick={() => setTab("transfer")}
                                    icon={Building2}
                                    label="Bank Transfer"
                                />
                            </div>

                            {tab === "card" && (
                                <div className="space-y-3" data-testid="payment-card-form">
                                    {customerEmail && (
                                        <p className="text-[11.5px] text-muted-foreground">
                                            Receipt to <span className="font-medium text-foreground">{customerEmail}</span>
                                        </p>
                                    )}
                                    <Field label="Card number">
                                        <input
                                            data-testid="payment-card-number"
                                            inputMode="numeric"
                                            placeholder="0000 0000 0000 0000"
                                            value={card.number}
                                            onChange={(e) =>
                                                setCard((c) => ({ ...c, number: formatCardNumber(e.target.value) }))
                                            }
                                            className="flex-1 bg-transparent outline-none text-[15px] tracking-wider"
                                        />
                                    </Field>
                                    <Field label="Cardholder name">
                                        <input
                                            data-testid="payment-card-name"
                                            placeholder="Full name on card"
                                            value={card.name}
                                            onChange={(e) => setCard((c) => ({ ...c, name: e.target.value }))}
                                            className="flex-1 bg-transparent outline-none text-[15px]"
                                        />
                                    </Field>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Field label="Expiry">
                                            <input
                                                data-testid="payment-card-expiry"
                                                inputMode="numeric"
                                                placeholder="MM/YY"
                                                value={card.expiry}
                                                onChange={(e) => setCard((c) => ({ ...c, expiry: formatExpiry(e.target.value) }))}
                                                className="flex-1 bg-transparent outline-none text-[15px]"
                                            />
                                        </Field>
                                        <Field label="CVV">
                                            <input
                                                data-testid="payment-card-cvv"
                                                inputMode="numeric"
                                                placeholder="123"
                                                value={card.cvv}
                                                onChange={(e) =>
                                                    setCard((c) => ({ ...c, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) }))
                                                }
                                                className="flex-1 bg-transparent outline-none text-[15px] tracking-widest"
                                            />
                                        </Field>
                                    </div>
                                    {cardErr && (
                                        <p data-testid="payment-card-error" className="text-[12px] text-destructive">
                                            {cardErr}
                                        </p>
                                    )}
                                </div>
                            )}

                            {tab === "transfer" && (
                                <div data-testid="payment-transfer-form">
                                    <div className="rounded-2xl border border-primary/25 bg-primary/5 p-4 text-center">
                                        <p className="text-[11.5px] text-muted-foreground">Send exactly</p>
                                        <p className="mt-0.5 font-display text-[22px] font-semibold">{fmt(amount)}</p>
                                        <p className="mt-1 text-[11px] text-primary font-medium">
                                            Account expires in <span data-testid="payment-transfer-countdown">{mmss}</span>
                                        </p>
                                    </div>
                                    <div className="mt-3 space-y-2">
                                        <CopyRow label="Bank" value={account.bank} testId="payment-transfer-bank" copyable={false} />
                                        <CopyRow label="Account number" value={account.number} testId="payment-transfer-account" />
                                        <CopyRow label="Account name" value={account.name} testId="payment-transfer-name" copyable={false} />
                                    </div>
                                    <p className="mt-3 text-[11.5px] text-muted-foreground">
                                        Use your banking app to make the transfer, then tap "I have paid".
                                    </p>
                                </div>
                            )}

                            {refundNote && <div className="mt-3" data-testid="payment-refund-note">{refundNote}</div>}
                        </>
                    )}
                </div>

                {/* Footer / Action */}
                {phase === "idle" && (
                    <div className="px-5 pt-3 shrink-0 border-t border-border/60 mt-3">
                        {tab === "card" ? (
                            <button
                                data-testid="payment-card-pay-btn"
                                onClick={handleCardPay}
                                className="w-full h-13 py-3.5 rounded-2xl text-primary-foreground font-medium text-[14.5px] active:scale-[0.98] transition-transform"
                                style={{
                                    background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                    boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                                }}
                            >
                                Pay {fmt(amount)}
                            </button>
                        ) : (
                            <button
                                data-testid="payment-transfer-paid-btn"
                                onClick={handleClaimedTransfer}
                                disabled={secondsLeft <= 0}
                                className="w-full py-3.5 rounded-2xl text-primary-foreground font-medium text-[14.5px] active:scale-[0.98] transition-transform disabled:opacity-50"
                                style={{
                                    background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                    boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                                }}
                            >
                                {secondsLeft <= 0 ? "Window expired" : "I have paid"}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const TabBtn = ({ active, onClick, icon: Icon, label, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className={[
            "flex-1 h-12 rounded-2xl border inline-flex items-center justify-center gap-2 text-[12.5px] font-medium transition-colors",
            active
                ? "border-primary bg-primary/5 text-foreground"
                : "border-border bg-secondary text-muted-foreground hover:bg-muted",
        ].join(" ")}
        aria-pressed={active}
    >
        <Icon size={14} />
        {label}
    </button>
);

const Field = ({ label, children }) => (
    <label className="block">
        <span className="block text-[11.5px] font-medium text-muted-foreground mb-1 ml-1">{label}</span>
        <span className="flex items-center h-12 px-3.5 rounded-xl border border-border bg-secondary focus-within:bg-background focus-within:border-primary transition-colors">
            {children}
        </span>
    </label>
);

const CopyRow = ({ label, value, copyable = true, testId }) => {
    const [copied, setCopied] = useState(false);
    const onCopy = () => {
        if (!copyable) return;
        navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
        });
    };
    return (
        <div className="flex items-center gap-3 p-3 rounded-xl border border-border bg-secondary" data-testid={testId}>
            <div className="min-w-0 flex-1">
                <div className="text-[10.5px] text-muted-foreground uppercase tracking-wider">{label}</div>
                <div className="font-mono text-[13.5px] font-semibold truncate">{value}</div>
            </div>
            {copyable && (
                <button
                    type="button"
                    onClick={onCopy}
                    className="h-9 w-9 rounded-lg bg-background flex items-center justify-center text-muted-foreground hover:text-foreground"
                    aria-label="Copy"
                >
                    {copied ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Copy size={14} />}
                </button>
            )}
        </div>
    );
};

export default PaymentSheet;
